import type { MeetingStatusBadgeColor } from "@/components/meetings/meetingStatusUtils";
import {
  getConfiguredMeetingTypeDefinition,
  getMeetingWorkspaceConfig,
  listConfiguredMeetingTypes,
} from "@/components/meetings/meetingWorkspaceConfigRuntime";

/** Octopus paraplanner meeting types (Plannr milestone sessions). */
export const OCTOPUS_MEETING_TYPES = [
  "intro_call",
  "starter_session",
  "planning_session",
  "advice_session",
] as const;

export type OctopusMeetingType = (typeof OCTOPUS_MEETING_TYPES)[number];

/** Tab panels available in the Meetings app detail view. */
export type MeetingTabId =
  | "overview"
  | "readiness"
  | "briefing"
  | "notes"
  | "outcome"
  | "capture"
  | "transcript"
  | "summary"
  | "extraction"
  | "observations"
  | "artefacts"
  | "actions"
  | "crm_updates";

/** Progeny Client test — matches API `CLIENT_TEST_TABS` in workspace defaults. */
export const CLIENT_TEST_TABS: MeetingTabId[] = [
  "overview",
  "briefing",
  "capture",
  "transcript",
  "summary",
];

/** Default lifecycle tab order for capture-pipeline meetings. */
export const CAPTURE_PIPELINE_TABS: MeetingTabId[] = [
  "overview",
  "briefing",
  "capture",
  "transcript",
  "summary",
  "extraction",
  "observations",
  "artefacts",
  "actions",
  "crm_updates",
];

/** Detail view component key — extensible per tenant/type later. */
export type MeetingDetailViewKey = "octopus" | "capture";

export interface MeetingTypeDefinition {
  key: string;
  label: string;
  tabs: MeetingTabId[];
  badgeColor: MeetingStatusBadgeColor;
  detailView: MeetingDetailViewKey;
}

const TAB_LABELS: Record<MeetingTabId, string> = {
  overview: "Overview",
  readiness: "Readiness",
  briefing: "Briefing",
  notes: "Notes",
  outcome: "Outcome",
  capture: "Capture",
  transcript: "Transcript",
  summary: "Summary",
  extraction: "Extractions",
  observations: "Observations",
  artefacts: "Artefacts",
  actions: "Actions",
  crm_updates: "CRM Updates",
};

const OCTOPUS_TABS: MeetingTabId[] = ["overview", "readiness", "briefing", "notes"];

const BUILTIN_MEETING_TYPES: Record<string, MeetingTypeDefinition> = {
  client_meeting: {
    key: "client_meeting",
    label: "Client meeting",
    tabs: ["overview", "briefing", "notes"],
    badgeColor: "indigo",
    detailView: "octopus",
  },
  intro_call: {
    key: "intro_call",
    label: "Intro call",
    tabs: ["overview", "briefing", "notes", "outcome"],
    badgeColor: "sky",
    detailView: "octopus",
  },
  starter_session: {
    key: "starter_session",
    label: "Starter session",
    tabs: ["overview", "briefing", "notes", "outcome"],
    badgeColor: "indigo",
    detailView: "octopus",
  },
  planning_session: {
    key: "planning_session",
    label: "Planning session",
    tabs: OCTOPUS_TABS,
    badgeColor: "violet",
    detailView: "octopus",
  },
  advice_session: {
    key: "advice_session",
    label: "Advice session",
    tabs: OCTOPUS_TABS,
    badgeColor: "rose",
    detailView: "octopus",
  },
  annual_review: {
    key: "annual_review",
    label: "Annual review",
    tabs: [...CAPTURE_PIPELINE_TABS],
    badgeColor: "violet",
    detailView: "capture",
  },
  first_meeting: {
    key: "first_meeting",
    label: "First meeting",
    tabs: CAPTURE_PIPELINE_TABS.filter((t) => t !== "briefing"),
    badgeColor: "sky",
    detailView: "capture",
  },
  phone_checkin: {
    key: "phone_checkin",
    label: "Phone check-in",
    tabs: CAPTURE_PIPELINE_TABS.filter((t) => t !== "briefing"),
    badgeColor: "amber",
    detailView: "capture",
  },
  other: {
    key: "other",
    label: "Other",
    tabs: CAPTURE_PIPELINE_TABS.filter((t) => t !== "briefing"),
    badgeColor: "zinc",
    detailView: "capture",
  },
  internal: {
    key: "internal",
    label: "Internal",
    tabs: ["overview", "capture", "transcript", "summary", "actions"],
    badgeColor: "zinc",
    detailView: "capture",
  },
  client_test: {
    key: "client_test",
    label: "Client test",
    tabs: CLIENT_TEST_TABS,
    badgeColor: "sky",
    detailView: "capture",
  },
};

const customMeetingTypes: Record<string, MeetingTypeDefinition> = {};

export function registerMeetingType(definition: MeetingTypeDefinition): void {
  customMeetingTypes[definition.key] = definition;
}

/** True when tab strip should not use the generic unknown-type fallback (before workspace config loads). */
export function hasStableMeetingTypeTabs(typeKey: string | null | undefined): boolean {
  const key = (typeKey ?? "").trim().toLowerCase();
  if (getConfiguredMeetingTypeDefinition(key)) return true;
  if (getMeetingWorkspaceConfig()) return true;
  if (!key) return true;
  if (key in BUILTIN_MEETING_TYPES) return true;
  return Boolean(customMeetingTypes[key]);
}

/** Untyped and unknown meetings resolve like the API: internal tabs only, never the full pipeline. */
export function getMeetingTypeDefinition(typeKey: string | null | undefined): MeetingTypeDefinition {
  const key = (typeKey ?? "").trim().toLowerCase() || "internal";
  const fromServer = getConfiguredMeetingTypeDefinition(key);
  if (fromServer) return fromServer;
  if (customMeetingTypes[key]) return customMeetingTypes[key];
  if (key in BUILTIN_MEETING_TYPES) return BUILTIN_MEETING_TYPES[key]!;
  return {
    key,
    label: key
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" "),
    tabs: getMeetingTypeDefinition("internal").tabs,
    badgeColor: "zinc",
    detailView: "capture",
  };
}

export function usesCapturePipeline(typeKey: string | null | undefined): boolean {
  return getMeetingTypeDefinition(typeKey).detailView === "capture";
}

export function isOctopusMeetingType(typeKey: string | null | undefined): boolean {
  return OCTOPUS_MEETING_TYPES.includes((typeKey ?? "").trim().toLowerCase() as OctopusMeetingType);
}

export function meetingTabLabel(tabId: MeetingTabId): string {
  return TAB_LABELS[tabId];
}

const MEETING_TAB_ID_SET = new Set<string>(Object.keys(TAB_LABELS));

export function isMeetingTabId(value: string | null | undefined): value is MeetingTabId {
  return Boolean(value && MEETING_TAB_ID_SET.has(value));
}

export function allMeetingTypeDefinitions(): MeetingTypeDefinition[] {
  const configured = listConfiguredMeetingTypes();
  if (configured.length) {
    return [...configured].sort((a, b) => a.label.localeCompare(b.label));
  }
  const keys = new Set([...Object.keys(BUILTIN_MEETING_TYPES), ...Object.keys(customMeetingTypes)]);
  return [...keys].map((k) => getMeetingTypeDefinition(k)).sort((a, b) => a.label.localeCompare(b.label));
}
